import { useEffect, useRef } from "react";
import SimpleBar from "simplebar-react";
import consts from "shared/consts";
import { CanvasTagId, CanvasTemplateMinimal, CanvasTemplateTag } from "shared/datamodel/schemas/canvas-template";
import TemplatesModalCovers from "../shared/templates-modal-covers";
import style from "../../templates-modal.module.css";
import type { TemplatesSection, CoverType } from "../../templates-modal";
import { TemplateSearchHeader } from "./template-search-header";
import { TemplateTagList } from "./template-tag-list";
import { TemplateSectionList } from "./template-section-list";

interface TemplateGalleryViewProps {
  sections: TemplatesSection[];
  tags: CanvasTemplateTag[];
  searchTerm: string;
  selectedTag: { id: CanvasTagId; name: string };
  coverType: CoverType | null;
  showUpgradeCTA?: boolean;
  isCimReorderEnabled: boolean;
  syncedTemplatesSectionId: number;
  shouldShowSyncedOnTop: boolean;
  accountName: string;
  onSearch: (searchTerm: string) => void;
  onTagClicked: (tagId: CanvasTagId, tagName: string) => void;
  onTemplateClick: (template: CanvasTemplateMinimal) => void;
  onPreviewTemplate: (template: CanvasTemplateMinimal) => void;
  onSeeAllClick: (tagId: number, tagName: string) => void;
  onUpgradeCTAClick: (source: string) => void;
  onEditTemplate: (template: CanvasTemplateMinimal) => void;
  onDeleteTemplate: (templateId: string) => void;
  onCreateTemplate?: () => void;
}

export function TemplateGalleryView({
  sections,
  tags,
  searchTerm,
  selectedTag,
  coverType,
  showUpgradeCTA,
  isCimReorderEnabled,
  syncedTemplatesSectionId,
  shouldShowSyncedOnTop,
  accountName,
  onSearch,
  onTagClicked,
  onTemplateClick,
  onPreviewTemplate,
  onSeeAllClick,
  onUpgradeCTAClick,
  onEditTemplate,
  onDeleteTemplate,
  onCreateTemplate,
}: TemplateGalleryViewProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset scroll position when selected tag changes
    if (scrollContainerRef.current) {
      const scrollElement = scrollContainerRef.current.querySelector(".simplebar-content-wrapper");
      if (scrollElement) {
        scrollElement.scrollTop = 0;
      }
    }
  }, [selectedTag.id]);

  return (
    <>
      <TemplateSearchHeader searchTerm={searchTerm} onSearch={onSearch} />
      <div className={style.content}>
        <TemplateTagList
          tags={tags}
          selectedTag={selectedTag}
          showUpgradeCTA={showUpgradeCTA}
          syncedTemplatesSectionId={syncedTemplatesSectionId}
          shouldShowSyncedOnTop={shouldShowSyncedOnTop}
          accountName={accountName}
          onTagClicked={onTagClicked}
          onUpgradeCTAClick={onUpgradeCTAClick}
        />
        {coverType ? (
          <TemplatesModalCovers
            coverType={coverType}
            onUpgradeCTAClick={onUpgradeCTAClick}
            emptyTemplatesType={selectedTag.id === consts.MY_TEMPLATES_TAG_ID ? "personal" : "account"}
            onCreateTemplate={onCreateTemplate}
            searchTerm={searchTerm}
          />
        ) : (
          <div className={style.templatesScrollContainer} ref={scrollContainerRef}>
            <SimpleBar
              scrollbarMaxSize={70}
              style={{
                maxHeight: isCimReorderEnabled ? "99%" : "100%",
              }}
            >
              <div className={style.templatesContainer}>
                <TemplateSectionList
                  sections={sections}
                  searchTerm={searchTerm}
                  isCimReorderEnabled={isCimReorderEnabled}
                  syncedTemplatesSectionId={syncedTemplatesSectionId}
                  onTemplateClick={onTemplateClick}
                  onPreviewTemplate={onPreviewTemplate}
                  onSeeAllClick={onSeeAllClick}
                  onEditTemplate={onEditTemplate}
                  onDeleteTemplate={onDeleteTemplate}
                />
              </div>
            </SimpleBar>
          </div>
        )}
      </div>
    </>
  );
}
